import React, { useEffect, useMemo, useState } from 'react';

import useLabOrderStore from '../../stores/labOrderStore';
import useProcedureStore from '../../stores/procedureStore';
import { usePermissions } from '../../context/PermissionContext';
import { STAFF_ROLES } from '../../constants/staff';
import { plusDaysISO, todayISO } from '../common/ValidityControl';
import { labStatusLabel, isStandingLabOrder } from '../../constants/lab';
import LabAdvisePanel from './LabAdvisePanel';
import LabListPicker from './LabListPicker';
import LabSummaryCard from './LabSummaryCard';
import { labStatusColor, lastDoneByProcedure, lastDoneLabel } from './labSummaryProjection';
import { procedurePickerDeptAnchor } from './departmentResolution';

/**
 * Lab-in-prescription surfaces (lab module ON).
 *
 *   <LabTestsField/>  — the always-visible "Tests" row inside the prescription.
 *                       Shows flag chips (🧪 lab order / 📄 outside), a one-tap
 *                       catalog quick-add, and a "Manage" button that opens…
 *   <LabPanel/>       — the focused overlay: the in-house advise/summary surface
 *                       (reuses LabAdvisePanel verbatim) plus an "outside tests"
 *                       section that edits the prescription's investigations[].
 *
 * Model:
 *   • In-house tests        → real LabOrders in labOrderStore (byVisit). Reception assigns.
 *   • "Suggested outside"    → free-text strings in the form's `investigations[]` (print only).
 *   • Both print identically on the Rx — handleSave merges the order names in at save time.
 *
 * rx-r3: on the DOCTOR's prescription the in-house ordering affordances are
 * HIDDEN (see useDoctorRxSurface) and replaced by a text-only LabListPicker.
 * Reception's affordances are unchanged. Nothing here is deleted.
 */

// Statuses that have left the "actively being advised" stage — they belong in
// the Lab Summary card, not the prescription's Tests row.
const TERMINAL_LAB = new Set(['completed', 'paid', 'cancelled']);

// The inline "Tests:" row shows ONLY this visit's NON-TERMINAL advised in-house
// orders (planned / awaiting / in-progress). Completed/cancelled + any
// patient-history rows live in the Lab Summary card — never inline / on the Rx.
const liveInHouse = (orders) =>
  (orders || []).filter(
    (o) => !TERMINAL_LAB.has(o.status) && !isStandingLabOrder(o) && !o.from_history,
  );

/**
 * SINGLE SOURCE OF TRUTH for "the visit's tests" (CLAUDE.md derived-value rule):
 * the inline Tests row, the "(N tests)" count, and the printed PDF must all derive
 * from this one projection so they can never disagree.
 *   - inHouse / external: this visit's LIVE (non-terminal) lab orders, split by tag.
 *   - outsideStrings: free-text investigations with NO order on the visit in ANY
 *     status (so a cancelled/done test's leftover string never shows or prints).
 *   - printableNames: unique(live order names ∪ outsideStrings) — what prints/counts.
 */
export function visitTestProjection(orders, investigations) {
  const live = liveInHouse(orders);
  const orderNamesAll = new Set((orders || []).map((o) => normalizeLabName(o.test_name)));
  const outsideStrings = (investigations || []).filter(
    (s) => !orderNamesAll.has(normalizeLabName(s)),
  );
  return {
    inHouse: live.filter((o) => !o.is_external),
    external: live.filter((o) => o.is_external),
    outsideStrings,
    printableNames: [
      ...new Set([...live.map((o) => o.test_name).filter(Boolean), ...outsideStrings]),
    ],
  };
}

// ── Catalog matching (revenue-leak guard) ──────────────────────────────
// Normalize a test name for comparison: lowercase, strip punctuation, collapse
// spaces. Used to spot when an "outside" free-text test is actually in the lab.
export const normalizeLabName = (s) =>
  String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Find the catalog lab test an outside free-text name corresponds to, if any.
 * Matches on normalized name or any catalog alias. Returns the test or null.
 */
export function findCatalogMatch(name, labTests) {
  const n = normalizeLabName(name);
  if (!n) return null;
  return (
    labTests.find((t) => {
      if (normalizeLabName(t.name) === n) return true;
      const aliases = t.aliases || t.alias || [];
      return Array.isArray(aliases) && aliases.some((a) => normalizeLabName(a) === n);
    }) || null
  );
}

/**
 * rx-r3 owner ruling — "picker only, and hide lab order from prescription".
 *
 * On the DOCTOR's prescription a test is PLAIN TEXT on the Rx plus a picker
 * that inserts a catalogue test's NAME as text. No lab ORDER may be raised
 * from that surface by any path — reception raises the orders.
 *
 * The in-house ordering controls are HIDDEN, never deleted: `quickAdd`,
 * `confirmPending`, the `pending` state + its expiry UI, `promoteOutside` and
 * the overlay's `LabAdvisePanel` all stay wired, so anything already on the
 * visit (an advised order, a pending test, an already-promoted outside test)
 * still displays, still round-trips and is still removable. Nothing is
 * stranded, and the paths survive intact for the later "lab order and
 * tracking" revisit.
 *
 * HARD BOUNDARY — nothing changes for RECEPTION. The prescription form also
 * renders for non-doctors (VisitActionsPanel's non-doctor branch), so the gate
 * keys on the DOCTOR context — not on the component and not on the lab feature
 * flag. A receptionist keeps every ordering affordance they have today.
 */
function useDoctorRxSurface() {
  const { role } = usePermissions();
  return String(role || '').toUpperCase() === STAFF_ROLES.DOCTOR;
}

function LabChip({ tone, children, onRemove, onPromote, title, dotColor }) {
  const cls =
    tone === 'lab'
      ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 border-teal-300 dark:border-teal-700'
      : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700';
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}
    >
      {dotColor
        ? <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} aria-hidden="true" />
        : (tone === 'lab' ? '🧪' : '📄')}
      {' '}{children}
      {/* FIX-3 (owner ruling) — the "Outside" text badge (FE-5, S3 AC3) is
          removed: the amber `tone="outside"` fill vs the teal `tone="lab"`
          fill already IS the in-house/outside signal, and the badge was
          extra noise on top of a colour that already carries the meaning.
          No replacement chrome — the chip keeps its name + the existing
          `title`/`onPromote`/`onRemove` affordances. */}
      {onPromote && (
        <button
          type="button"
          onClick={onPromote}
          title="This test is in your lab — create a lab order instead of printing it for outside"
          className="ml-0.5 rounded-full bg-teal-600 text-white px-1.5 py-0.5 text-[10px] font-bold hover:bg-teal-700 leading-none"
        >
          → to lab
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 opacity-70 hover:opacity-100 leading-none"
          aria-label="Remove"
        >
          ✕
        </button>
      )}
    </span>
  );
}

/**
 * The inline "Tests" row shown in the prescription when the lab module is on.
 */
export function LabTestsField({
  visitId, patientId, visit, investigations, setInvestigations, isDark = false,
  // Optional node rendered at the END of the "🧪 Tests:" line. The prescription
  // pad folds its lab-test template group in there (D18) so the pad does not
  // need a Tests row of its own. Nothing lab-related is read from it.
  headerRight = null,
  // D25/F6 — the doctor's LAB TESTS templates, offered as rows inside the
  // lab-list picker. Passed straight through: nothing lab-related is read from
  // it here, and it is absent for every mount but the doctor's pad.
  templates = null,
  // FE-4 — the G1 `memory` object (`memory.types.INV`), passed straight
  // through to `LabListPicker`. Absent (null) for every mount but the
  // doctor's pad, exactly like `templates` — nothing lab-related is read
  // from it here either.
  memory = null,
  // FE-12 — forget a remembered test (G5). `onForgetMemory(row)` opens the
  // pad's OWN `ConfirmationModal` + Undo toast (this component owns none of
  // that UI); `finePointer` decides hover-reveal vs always-visible on
  // touch. Both passed straight through to `LabListPicker`, same posture as
  // `memory`/`templates` — absent for every mount but the doctor's pad.
  onForgetMemory = null,
  finePointer = true,
}) {
  // A5 — the quick-add "+ from lab…" picker is dept-SCOPED to the visit's
  // department (server-scoped, dept-tagged ∪ org-wide). Falsy/EMERGENCY anchor
  // → null → full catalog. Never reads the unscoped admin `procedures` slice.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const anchor = procedurePickerDeptAnchor(visit);
  // rx-r3 — see useDoctorRxSurface. Doctor → in-house ordering hidden (picker
  // only). Anyone else on this form (reception) → unchanged.
  const isDoctorRx = useDoctorRxSurface();
  const canOrderFromRx = !isDoctorRx;
  const { byVisit, byPatient, advise, fetchForVisit, fetchRecentForPatient, cancel } = useLabOrderStore();
  const [showPanel, setShowPanel] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  // Inline quick-add: a picked test pending an (editable) expiry before advising.
  const [pending, setPending] = useState(null); // { procedureId, name, expiresAt }

  useEffect(() => {
    fetchProceduresForDept(anchor);
    // The inline Tests row is THIS-VISIT only (active advises). The patient-scoped
    // history lives in the separate Lab Summary card — not here.
    if (visitId) fetchForVisit(visitId, { includeAdvised: true });
  }, [fetchProceduresForDept, anchor, fetchForVisit, visitId]);

  const labTests = useMemo(
    () => pickerProcedures.filter((p) => p.category === 'lab' && p.active !== false),
    [pickerProcedures]
  );
  const orders = useMemo(() => byVisit[visitId] || [], [byVisit, visitId]);
  // SINGLE SOURCE — the same projection the count + printed PDF use, so the inline
  // row, the "(N tests)" count, and the Rx can never disagree. inHouse (🧪 teal) /
  // external (📄 amber) are this visit's LIVE orders; outsideTests are free-text
  // strings with NO order on the visit (any status — a cancelled/done test's
  // leftover string is excluded so it can't be re-promoted into a duplicate).
  const { inHouse, external, outsideStrings: outsideTests, printableNames } = useMemo(
    () => visitTestProjection(orders, investigations),
    [orders, investigations]
  );
  // What the picker must NOT offer. `visitTestProjection` drops an
  // investigation string whose name matches ANY order on the visit — in ANY
  // status, terminal included — so offering such a test would be a click that
  // silently does nothing (the string lands in investigations[] and is
  // immediately projected away). Offer only what can actually be inserted.
  const alreadyOnVisit = useMemo(
    () => [...printableNames, ...orders.map((o) => o.test_name).filter(Boolean)],
    [printableNames, orders]
  );
  // Normalized catalogue names — a chip whose text is in the org's lab list is
  // tagged "From your lab list". Plain name membership, NOT `findCatalogMatch`
  // (that exists to resolve aliases for the — now hidden — promote path).
  const catalogNames = useMemo(
    () => new Set(labTests.map((t) => normalizeLabName(t.name))),
    [labTests]
  );
  const advisedIds = useMemo(
    () => new Set(inHouse.map((o) => o.catalog_procedure_id)),
    [inHouse]
  );
  // Catalog tests not already advised on this visit — the quick-add options.
  const quickOptions = useMemo(
    () => labTests.filter((t) => !advisedIds.has(t.procedure_id)),
    [labTests, advisedIds]
  );
  // Repeat-test awareness: when was each catalog test last completed for this patient.
  const lastDone = useMemo(
    () => lastDoneByProcedure(byPatient[patientId]?.orders || []),
    [byPatient, patientId]
  );

  const effVisit = visit || { visit_id: visitId, patient_id: patientId };

  const quickAdd = async (procedureId, expiresAt = plusDaysISO(30)) => {
    if (!procedureId) return;
    setBusyId(procedureId);
    setErr(null);
    const r = await advise({
      visit_id: visitId,
      patient_id: patientId,
      catalog_procedure_id: procedureId,
      patient_name: effVisit.patient_name || effVisit.name || undefined,
      queue_number: effVisit.queue_number,
      expires_at: expiresAt || null,
      standing: false,
    });
    setBusyId('');
    if (!r.success) setErr(r.error || 'Could not add the lab test.');
  };

  const confirmPending = async () => {
    if (!pending) return;
    await quickAdd(pending.procedureId, pending.expiresAt);
    setPending(null);
  };

  const removeOutside = (name) =>
    setInvestigations((prev) => prev.filter((x) => x !== name));

  // rx-r3 — the ONLY thing the lab-list picker does: append the catalogue
  // test's NAME to investigations[] as plain text. No order, no store call.
  // Deduped on the normalized name so a pick can't double a test the parser
  // already resolved from the doctor's typing.
  const pickFromLabList = (test) => {
    const name = test?.name;
    if (!name) return;
    setInvestigations((prev) => {
      const list = prev || [];
      return list.some((x) => normalizeLabName(x) === normalizeLabName(name))
        ? list
        : [...list, name];
    });
  };

  // Remove an in-house order: only an advised, unassigned order can be cancelled
  // by the doctor. Once reception has assigned/started it, it's locked.
  const removeInHouse = async (o) => {
    setNote(null);
    if (o.status !== 'advised' || o.assigned_to?.member_id) {
      setNote('Lab already in progress at reception — cancel it from the Lab queue.');
      return;
    }
    setBusyId(o.lab_order_id);
    setErr(null);
    const r = await cancel(o.lab_order_id, { reason: 'Removed from prescription' });
    setBusyId('');
    if (r?.success === false) { setErr(r.error || 'Could not cancel the lab order.'); return; }
    await fetchForVisit(visitId, { includeAdvised: true });
    if (patientId) fetchRecentForPatient(patientId);
  };

  // Revenue-leak guard: an outside test that's actually in the catalog can be
  // promoted to a real lab order in one click (advise + drop the outside string).
  const promoteOutside = async (name, test) => {
    await quickAdd(test.procedure_id);
    removeOutside(name);
  };

  const fieldCls =
    'rounded-lg px-2.5 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const hasAny = inHouse.length > 0 || external.length > 0 || outsideTests.length > 0;

  return (
    <div data-tour="investigations" className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">🧪 Tests:</span>

        {inHouse.map((o) => (
          <LabChip
            key={o.lab_order_id}
            tone="lab"
            dotColor={labStatusColor(o.status)}
            title={`${labStatusLabel(o.status)} · ${o.assigned_to?.member_id ? 'assigned' : 'awaiting assignment'}`}
            onRemove={() => removeInHouse(o)}
          >
            {o.test_name}
          </LabChip>
        ))}
        {external.map((o) => (
          <LabChip
            key={o.lab_order_id}
            tone="outside"
            title={`Outside · ${labStatusLabel(o.status)}${o.expires_at ? ` · valid till ${String(o.expires_at).slice(0, 10)}` : ''}`}
            onRemove={() => removeInHouse(o)}
          >
            {o.test_name}
          </LabChip>
        ))}
        {outsideTests.map((name) => {
          const match = findCatalogMatch(name, labTests);
          const fromLabList = catalogNames.has(normalizeLabName(name));
          return (
            <LabChip
              key={`out-${name}`}
              tone="outside"
              title={
                isDoctorRx
                  ? (fromLabList
                      ? 'From your lab list'
                      : 'Printed on the prescription only. To track it, add it under Tests done outside.')
                  : (match ? `${name} is in your lab — create a lab order?` : 'Printed on the prescription only. To track it, add it under Tests done outside.')
              }
              onRemove={() => removeOutside(name)}
              onPromote={canOrderFromRx && match ? () => promoteOutside(name, match) : undefined}
            >
              {name}
            </LabChip>
          );
        })}
        {!hasAny && (
          <span className="text-xs text-slate-400 dark:text-slate-500 italic">No tests added yet.</span>
        )}
        {headerRight}
      </div>

      {/* rx-r3 — text-only lab-list picker: inserts the catalogue test's NAME
          into investigations[]. Presentational; it cannot reach the lab order
          store (see LabListPicker). `labTests` is the dept-scoped slice already
          fetched above — the picker adds no store and fires no request. */}
      <LabListPicker
        labTests={labTests}
        existingNames={alreadyOnVisit}
        onPick={pickFromLabList}
        isDark={isDark}
        templates={templates}
        memory={memory}
        onForgetMemory={onForgetMemory}
        finePointer={finePointer}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {/* Inline quick-add — pick a test, confirm its (editable) valid-until
            date, advise. HIDDEN on the doctor's prescription (rx-r3): it is the
            in-house lab-ORDER entry point. Left mounted for everyone else so
            reception's affordances are bit-for-bit unchanged. */}
        {canOrderFromRx && (
          <select
            className={`${fieldCls} min-w-[170px]`}
            value=""
            disabled={!!busyId || quickOptions.length === 0 || !!pending}
            onChange={(e) => {
              const t = quickOptions.find((x) => x.procedure_id === e.target.value);
              if (t) setPending({ procedureId: t.procedure_id, name: t.name, expiresAt: plusDaysISO(30) });
            }}
          >
            <option value="">{busyId ? 'Adding…' : '+ from lab…'}</option>
            {quickOptions.map((t) => {
              const done = lastDoneLabel(lastDone[t.procedure_id]);
              return (
                <option key={t.procedure_id} value={t.procedure_id}>
                  {t.name} — ₹{Number(t.default_cost || 0).toFixed(0)}{done ? ` · ${done}` : ''}
                </option>
              );
            })}
          </select>
        )}

        <button
          type="button"
          onClick={() => setShowPanel(true)}
          className="rounded-lg px-3 py-2 text-sm font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          ✎ Manage tests
        </button>

        {!pending && (
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            {isDoctorRx
              ? 'Type a test, or add one from your lab list. Reception raises the orders.'
              : 'Pick from your lab → a lab order · use Manage to add tests done outside'}
          </span>
        )}
      </div>

      {/* Editable valid-until before the order is created (per requirement). */}
      {pending && (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="font-semibold text-slate-700 dark:text-slate-200">🧪 {pending.name}</span>
          <span className="text-slate-500 dark:text-slate-400">valid until</span>
          <input
            type="date"
            value={pending.expiresAt}
            min={todayISO()}
            onChange={(e) => setPending((p) => ({ ...p, expiresAt: e.target.value }))}
            className={`${fieldCls} !px-2 !py-1`}
          />
          <button type="button" disabled={!!busyId} onClick={confirmPending} className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60">+ Advise</button>
          <button type="button" disabled={!!busyId} onClick={() => setPending(null)} className="rounded-lg px-2.5 py-1.5 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200">Cancel</button>
        </div>
      )}

      {err && <div className="text-xs text-rose-600 dark:text-rose-400">{err}</div>}
      {note && <div className="text-xs text-amber-600 dark:text-amber-400">{note}</div>}

      {/* Lab summary (review) lives right here in the Tests block — collapsed,
          patient-scoped. One cohesive lab home: advise above, review below.
          Self-gates to null when the patient has no labs (divider lives inside). */}
      <LabSummaryCard patientId={patientId} visitId={visitId} isDark={isDark} embedded />

      {showPanel && (
        <LabPanel
          visit={effVisit}
          visitId={visitId}
          patientId={patientId}
          investigations={investigations}
          setInvestigations={setInvestigations}
          isDark={isDark}
          onClose={() => setShowPanel(false)}
        />
      )}
    </div>
  );
}

/**
 * The focused "add tests" overlay — the in-house advise form (LabAdvisePanel,
 * list suppressed) + the outside-tests section. Review/manage of the full
 * lifecycle lives in the separate top-level LabSummaryCard, not here.
 */
export function LabPanel({ visit, visitId, patientId, investigations, setInvestigations, isDark = false, onClose }) {
  const [outsideInput, setOutsideInput] = useState('');
  const [outsideExpiry, setOutsideExpiry] = useState(plusDaysISO(30));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // A5 — same scoped slice/anchor as LabTestsField. Defensive re-fetch: the
  // overlay is usually opened from LabTestsField, which already fetched this
  // same anchor, so `fetchProceduresForDept` (single-flight) is a no-op here.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const { advise, fetchForVisit, fetchRecentForPatient, cancel, byVisit } = useLabOrderStore();
  // rx-r3 — same doctor-scoped gate as LabTestsField (this overlay is only
  // reachable from it). Reception's copy of the overlay is unchanged.
  const isDoctorRx = useDoctorRxSurface();
  const canOrderFromRx = !isDoctorRx;
  const effVisit = visit || { visit_id: visitId, patient_id: patientId };
  const anchor = procedurePickerDeptAnchor(effVisit);
  useEffect(() => {
    fetchProceduresForDept(anchor);
  }, [anchor, fetchProceduresForDept]);
  const labTests = useMemo(
    () => pickerProcedures.filter((p) => p.category === 'lab' && p.active !== false),
    [pickerProcedures]
  );
  // This visit's live external (done-outside) orders — shown so the doctor sees
  // what they've added, each with a validity + a remove (cancel) action.
  const externalLive = useMemo(
    () => (byVisit[visitId] || []).filter((o) => o.is_external && !TERMINAL_LAB.has(o.status)),
    [byVisit, visitId]
  );

  // A "done outside" test is now a TRACKED external lab order (validity +
  // extend/cancel + appears in the Lab Summary), not a print-only string.
  const addOutside = async () => {
    const v = outsideInput.trim();
    if (!v) return;
    setBusy(true);
    setErr(null);
    const r = await advise({
      visit_id: visitId,
      patient_id: patientId,
      is_external: true,
      test_name: v,
      patient_name: effVisit.patient_name || effVisit.name || undefined,
      queue_number: effVisit.queue_number,
      expires_at: outsideExpiry || null,
      standing: false,
    });
    setBusy(false);
    if (r.success) {
      setOutsideInput('');
      if (visitId) fetchForVisit(visitId, { includeAdvised: true });
      if (patientId) fetchRecentForPatient(patientId);
    } else {
      setErr(r.error || 'Could not add the outside test.');
    }
  };
  const cancelExternal = async (o) => {
    setBusy(true);
    setErr(null);
    const r = await cancel(o.lab_order_id, { reason: 'Removed from prescription' });
    setBusy(false);
    if (r?.success === false) { setErr(r.error || 'Could not remove the outside test.'); return; }
    if (visitId) fetchForVisit(visitId, { includeAdvised: true });
    if (patientId) fetchRecentForPatient(patientId);
  };
  // Legacy free-text outside strings (from before external tests were tracked).
  const removeOutside = (name) =>
    setInvestigations((prev) => prev.filter((x) => x !== name));

  // Promote an outside test that's actually in the catalog → real lab order.
  const promoteOutside = async (name, test) => {
    const r = await advise({
      visit_id: visitId,
      patient_id: patientId,
      catalog_procedure_id: test.procedure_id,
      patient_name: effVisit.patient_name || effVisit.name || undefined,
      queue_number: effVisit.queue_number,
      expires_at: plusDaysISO(30),
      standing: false,
    });
    if (r.success) {
      removeOutside(name);
      if (visitId) fetchForVisit(visitId);
    }
  };

  const fieldCls =
    'rounded-lg px-2.5 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">🧪 Add tests to the prescription</div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* In-house ADVISE form (catalog + editable expiry). Its list is suppressed —
              review/manage of the full lifecycle lives in the top Lab Summary card.
              HIDDEN on the doctor's prescription (rx-r3) — it is the in-house
              lab-ORDER entry point. Untouched for everyone else, and untouched
              at its OTHER mount (VisitActionsPanel's Lab tab), which is how
              reception raises orders. */}
          {canOrderFromRx && <LabAdvisePanel visit={effVisit} includeAdvised showList={false} />}

          {/* Outside tests — a TRACKED external order (validity + extend/cancel), done outside */}
          <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2">
              📄 Tests done outside <span className="font-normal text-slate-400 dark:text-slate-500">· tracked with a validity, not run in-house</span>
            </div>
            {(externalLive.length > 0 || investigations.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {externalLive.map((o) => (
                  <LabChip
                    key={`p-ext-${o.lab_order_id}`}
                    tone="outside"
                    title={`Outside · ${labStatusLabel(o.status)}${o.expires_at ? ` · valid till ${String(o.expires_at).slice(0, 10)}` : ''}`}
                    onRemove={() => cancelExternal(o)}
                  >
                    {o.test_name}
                  </LabChip>
                ))}
                {/* Legacy print-only strings from before external tests were tracked. */}
                {investigations.map((name) => {
                  const match = findCatalogMatch(name, labTests);
                  const fromLabList = labTests.some(
                    (t) => normalizeLabName(t.name) === normalizeLabName(name),
                  );
                  return (
                    <LabChip
                      key={`p-out-${name}`}
                      tone="outside"
                      title={
                        isDoctorRx
                          ? (fromLabList ? 'From your lab list' : 'Older print-only entry')
                          : (match ? `${name} is in your lab — create a lab order?` : 'Older print-only entry')
                      }
                      onRemove={() => removeOutside(name)}
                      onPromote={canOrderFromRx && match ? () => promoteOutside(name, match) : undefined}
                    >
                      {name}
                    </LabChip>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className={`${fieldCls} flex-1 min-w-[10rem]`}
                placeholder="e.g. Vitamin D, Genetic panel…"
                value={outsideInput}
                disabled={busy}
                onChange={(e) => setOutsideInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOutside(); } }}
              />
              <span className="text-xs text-slate-500 dark:text-slate-400">valid until</span>
              <input
                type="date"
                value={outsideExpiry}
                min={todayISO()}
                disabled={busy}
                onChange={(e) => setOutsideExpiry(e.target.value)}
                className={`${fieldCls} !px-2 !py-1`}
              />
              <button
                type="button"
                onClick={addOutside}
                disabled={busy || !outsideInput.trim()}
                className="rounded-lg px-3 py-2 text-sm font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
              >
                {busy ? 'Adding…' : 'Advise outside'}
              </button>
            </div>
            {err && <div className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{err}</div>}
          </div>

          {/* Role-dependent (rx-r3, PO-ratified): the doctor's overlay only
              records text, so it points back at the row's picker. Reception's
              overlay still hosts the in-house advise form, so it keeps the
              in-house-versus-outside distinction — the only place that
              audience is told it. Same `useDoctorRxSurface` gate as the
              ordering controls; no second way of asking the same question. */}
          <div className="text-xs rounded-lg px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300">
            {isDoctorRx
              ? 'Pick from your lab list, or close this and type the test name.'
              : 'In-house tests run in your lab. Outside tests are only recorded here — they aren\'t run or billed in-house.'}
          </div>
        </div>

        <div className="flex justify-end px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default LabPanel;
